/**
 * GatewayDetailSheet — right-side drawer for a single Thing (B0.44.4).
 *
 * Opens when the user clicks a row on /app/gateways (or from a link:
 * ?gateway=<id>&tab=<tab>). Organised in tabs so each view stays short
 * (progressive disclosure), opening on Overview:
 *
 * - Overview: status, the msg/s / last seen / cert stats and the throughput
 *   sparkline, the birth on one line (online since, firmware) with the
 *   networks folded to a summary line, the pending claim code and the scan-to-
 *   onboard QR codes while onboarding, and a callout when the firmware
 *   outgrows the product.
 * - Devices: "Connect a device to this gateway" (the wizard, pre-filled), the
 *   instruments with their live values (products that poll devices), and the
 *   Thing's own tag inventory (live values, add / edit / remove). On a gateway
 *   that polls instruments the inventory shows only when the gateway itself has
 *   tags: its data points are on its instruments. A small gateway opens every
 *   instrument's values at once, a big one only the first, and the open ones
 *   stay open across the tabs.
 *
 * Live (2026-10-30): the rate and its last 20 minutes come from the same read as
 * the gateways list (GET /v1/things/rates, every 15 s), "Last seen" is the
 * newest sign of life and its age moves on, and the header's refresh reads the
 * gateway, its rate, its instruments and their values again now.
 * - Protocols: config versions, drivers, serial ports, which protocols new
 *   devices may use (with the reasons for the others) and "Change product".
 * - Settings: rename / Simulate (the pencil), claim code, Measure now, change
 *   equipment type; and a separated danger zone, where Reset / Re-provision
 *   and Remove each ask in a ConfirmDialog first.
 */

import { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { keepPreviousData, useQuery, useQueryClient } from "@tanstack/react-query";
import { formatDistanceToNow } from "date-fns";
import {
  ArrowRight,
  Check,
  Copy,
  Cpu,
  Gauge,
  Loader2,
  Pencil,
  Plus,
  QrCode,
  Radar,
  RefreshCw,
  RotateCcw,
  Trash2,
  TriangleAlert,
  Wifi,
  Wrench,
  X,
} from "lucide-react";
import { QRCodeSVG } from "qrcode.react";
import { Sheet } from "@/lib/ui/sheet";
import { Badge } from "@/lib/ui/badge";
import { Button } from "@/lib/ui/button";
import { ConfirmDialog } from "@/lib/ui/confirm-dialog";
import { Input } from "@/lib/ui/input";
import { Skeleton } from "@/lib/ui/skeleton";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/lib/ui/tabs";
import { cn } from "@/lib/ui/cn";
import { SparklineCell, StatusPill } from "@/lib/widgets";
import { getProtocolLabel, useGateways, type GatewayRecord } from "@/lib/iot/gateways-store";
import { things, tags as tagsApi, isLiveApi, backendErrorMessage, useThingRatesQuery, type TagOption } from "@/lib/api";
import { formatRate, newestTime, rateByThing, rateSparkline, useNow, valuesReadFailing } from "@/lib/iot/gateway-live";
import { RefreshButton } from "@/components/iot/RefreshButton";
import { ValuesReadFailed } from "@/components/iot/ValuesReadFailed";
import { DATA_KIND_LABEL, type DataKind } from "@/lib/dashboard/data-kinds";
import { useToast } from "@/lib/ui/toast";
import { VSyncInstrumentPicker } from "@/components/iot/VSyncInstrumentPicker";
import { GatewayInstruments } from "@/components/iot/GatewayInstruments";
import { GatewayBirthPanel } from "@/components/iot/GatewayBirthPanel";
import { GatewayUpdateFetchPanel } from "@/components/iot/GatewayUpdateFetchPanel";
import { useGatewayDrivers } from "@/components/connect/gateway-queries";
import { connectToGatewayHref } from "@/components/connect/links";
import { deriveStatus, errorDetailOf, writeErrorMessage } from "@/lib/api";
import { productPollsDevices, productSwitchCandidate } from "@/lib/iot/drivers";
import { readBirth } from "@/lib/iot/gateway-birth";
import { readingQuality } from "@/lib/iot/reading-quality";
import { POINTS_PAGE_SIZE, latestByTag, type PointLatest } from "@/lib/iot/instrument-points";
import { usePointsPage } from "@/lib/iot/use-points-page";
import { PointsFilter, PointsPager } from "@/components/iot/PointsPaging";
import { TagRowsEditor, blankRow, namedRowsOf, toTagInputs, type TagRow } from "@/components/iot/ThingTagRows";

/** The sheet's tabs, also the values of ?tab= on the gateways page. */
export type GatewaySheetTab = "overview" | "devices" | "protocols" | "settings";
const TABS: GatewaySheetTab[] = ["overview", "devices", "protocols", "settings"];

export interface GatewayDetailSheetProps {
  gateway: GatewayRecord | null;
  onClose: () => void;
  /** The tab to open on (a link's ?tab=); default Overview. */
  initialTab?: string | null;
  /** The viewer may commission (connect devices, reset, remove). Default true. */
  canCommission?: boolean;
}

export function GatewayDetailSheet({ gateway, onClose, initialTab, canCommission = true }: GatewayDetailSheetProps) {
  const gateways = useGateways();
  const qc = useQueryClient();
  const toast = useToast();
  // msg/s is no longer on the list row (dropped from ListThings — roadmap #8:
  // it scanned readings per-Thing on every list paint). Fetch the single Thing
  // here (one cheap count in GetThing) for the live rate while the sheet is open.
  const freshThing = useQuery({
    queryKey: ["thing", gateway?.id],
    queryFn: () => things.get(gateway!.id),
    enabled: isLiveApi && !!gateway,
    // Gap D: poll fast (5s) while the device hasn't reported yet — no last_seen
    // and no msgs — so the sheet flips from "pending claim / awaiting data" to
    // live within seconds of the first telemetry, instead of only on a 30s beat
    // or a hard refresh. Back off to 30s once it's active.
    refetchInterval: (query) => {
      const t = query.state.data;
      const active = !!t?.last_seen || (t?.msg_per_sec ?? 0) > 0;
      return active ? 30_000 : 5_000;
    },
  });
  // The rate and its last 20 minutes: the gateways list's read (one cache entry, every 15 s), so the sheet and the
  // row agree. Without the rates (an API without them), GetThing's msg_per_sec.
  const ratesQ = useThingRatesQuery();
  // While the last read of the rates failed they are not known (React Query keeps the last good answer): GetThing's.
  const ratesData = ratesQ.isError ? undefined : ratesQ.data;
  const rates = rateByThing(ratesData);
  const rate = gateway ? rates?.get(gateway.id) : undefined;
  const liveMsgPerSec = rates ? (rate?.msg_per_sec ?? 0) : (freshThing.data?.msg_per_sec ?? gateway?.msgPerSec ?? 0);
  // Moves "Last seen" on between reads.
  const now = useNow(5_000);
  const [refreshing, setRefreshing] = useState(false);
  // The instruments whose values are open, kept per gateway across the tabs; null: the default set.
  const [openValues, setOpenValues] = useState<{ gatewayId: string; ids: Set<string> } | null>(null);
  // Multi-protocol: the product catalogue (does this product poll devices? is
  // there a product that covers what the firmware runs?) and the driver list.
  const productsQ = useQuery({
    queryKey: ["thing-products"],
    queryFn: () => things.products(),
    enabled: isLiveApi && !!gateway,
    staleTime: 5 * 60_000,
  });
  const isGatewayThing = freshThing.data?.kind === "gateway";
  const driversQ = useGatewayDrivers(gateway?.id, isGatewayThing);
  // The Devices tab's count: the same list (and cache key) the instruments panel reads.
  const instrumentsQ = useQuery({
    queryKey: ["things", "instruments", gateway?.id],
    queryFn: () => things.listAll({ gateway_id: gateway!.id, kind: "sensor" }),
    enabled: isLiveApi && !!gateway && isGatewayThing,
    // Each instrument's "data ... ago" (its last_seen) moves on while the sheet is open.
    refetchInterval: 30_000,
  });
  const [switchingProduct, setSwitchingProduct] = useState(false);
  const [switchError, setSwitchError] = useState<string | null>(null);
  // A device that has started reporting (polled Thing shows last_seen / msgs) is
  // no longer "pending claim", even if the snapshot prop still carries the old
  // claim code — hide the stale panel so provisioning stops looking broken (Gap D).
  const liveActive = isLiveApi && (!!freshThing.data?.last_seen || (freshThing.data?.msg_per_sec ?? 0) > 0);
  const [confirmDelete, setConfirmDelete] = useState(false);
  const [copied, setCopied] = useState(false);
  const startTab = (TABS as string[]).includes(initialTab ?? "") ? (initialTab as GatewaySheetTab) : "overview";
  // The tab belongs to the gateway it was picked on: another gateway starts on the link's tab.
  const [tabPick, setTabPick] = useState<{ id: string | undefined; tab: GatewaySheetTab } | null>(null);
  const tab = tabPick && tabPick.id === gateway?.id ? tabPick.tab : startTab;
  const setTab = (t: GatewaySheetTab) => setTabPick({ id: gateway?.id, tab: t });

  // Inline edit (rename + Simulate). null = view mode; otherwise the draft.
  const [edit, setEdit] = useState<{ name: string; simulate: boolean } | null>(null);
  const [saving, setSaving] = useState(false);
  const [regenerating, setRegenerating] = useState(false);
  // A freshly-minted claim, held locally so it renders the instant the mint
  // returns. The parent (devices.tsx) passes a SNAPSHOT of `gateway` captured
  // at row-click, and a things-query refetch clobbers the store copy — so
  // neither reliably flows the new code back into this prop. Cleared when the
  // selected Thing changes.
  const [mintedClaim, setMintedClaim] = useState<{ code: string; expiresAt: string } | null>(null);
  // Reconfigure ("Change equipment type") drawer — null = closed.
  const [reconfig, setReconfig] = useState<{ rows: TagRow[]; instrumentType: string } | null>(null);
  const [savingTags, setSavingTags] = useState(false);
  const [measuring, setMeasuring] = useState(false);
  const [confirmReprov, setConfirmReprov] = useState(false);

  // Reset transient UI whenever the selected Thing changes / the sheet reopens.
  useEffect(() => {
    setEdit(null);
    setConfirmDelete(false);
    setCopied(false);
    setMintedClaim(null);
    setReconfig(null);
    setConfirmReprov(false);
    setSwitchError(null);
  }, [gateway?.id]);

  if (!gateway) {
    return (
      <Sheet open={false} onClose={onClose} widthClass="max-w-xl" ariaLabel="Gateway detail">
        {null}
      </Sheet>
    );
  }

  // Prefer the locally-held minted code (see mintedClaim above) over the
  // snapshot/store prop, which lags or gets clobbered. Gap D: suppress the stale
  // prop code once the device has gone live (liveActive) so the "pending claim"
  // panel disappears on its own; a freshly *minted* code (reprovision) always shows.
  const claimCode = mintedClaim?.code ?? (liveActive ? undefined : gateway.claimCode);
  const claimCodeExpiresAt = mintedClaim?.expiresAt ?? (liveActive ? undefined : gateway.claimCodeExpiresAt);

  const copyCode = async () => {
    if (!claimCode) return;
    try {
      await navigator.clipboard.writeText(claimCode);
      setCopied(true);
      setTimeout(() => setCopied(false), 1500);
      toast.push("ok", "Claim code copied");
    } catch {
      /* clipboard permission */
    }
  };

  const handleRegenerate = async () => {
    if (regenerating) return;
    if (!isLiveApi) {
      // No-backend demo: fabricate a local stub code (never a real claim).
      const code = gateways.regenerateClaimCode(gateway.id);
      setMintedClaim({ code, expiresAt: new Date(Date.now() + 24 * 60 * 60 * 1000).toISOString() });
      toast.push("info", `New claim code minted for ${gateway.name}`);
      return;
    }
    setRegenerating(true);
    try {
      // The ONE real mint: POST /v1/things/{id}/claim → server code + 1h expiry.
      const claim = await things.claim(gateway.id);
      setMintedClaim({ code: claim.claim_code, expiresAt: claim.expires_at });
      gateways.setClaimCode(gateway.id, claim.claim_code, claim.expires_at);
      toast.push("ok", `New claim code minted for ${gateway.name}`);
    } catch (err) {
      toast.push("error", backendErrorMessage(err));
    } finally {
      setRegenerating(false);
    }
  };

  // Save the rename + Simulate edit. Live mode: PATCH /v1/things/{id}; both
  // modes mirror the change into the local gateways store + refetch.
  const saveEdit = async () => {
    if (!edit) return;
    const name = edit.name.trim();
    if (!name) return;
    if (isLiveApi) {
      setSaving(true);
      try {
        await things.update(gateway.id, { name, simulate: edit.simulate });
      } catch (err) {
        setSaving(false);
        toast.push("error", backendErrorMessage(err));
        return;
      }
      setSaving(false);
      void qc.invalidateQueries({ queryKey: ["things"] });
    }
    gateways.update(gateway.id, { name });
    setEdit(null);
    toast.push("ok", `Saved ${name}`);
  };

  // Confirmed in the ConfirmDialog (it shows the spinner while this runs).
  const handleDelete = async () => {
    // Live mode: DELETE /v1/things/{id} (204; cascades children + tags),
    // then refetch the things query. Stub mode: local store remove only.
    if (isLiveApi) {
      try {
        await things.remove(gateway.id);
      } catch (err) {
        toast.push("error", backendErrorMessage(err));
        return;
      }
      void qc.invalidateQueries({ queryKey: ["things"] });
    }
    gateways.remove(gateway.id);
    toast.push("ok", `Removed ${gateway.name}`);
    setConfirmDelete(false);
    onClose();
  };

  // Reconfigure: atomically replace the Thing's whole tag set (change equipment
  // type / re-import config → POST /v1/things/{id}/tags:replace). Requires
  // exactly one cycle-counter row so the emitted device config carries a
  // P_NO==1 trigger (else the VSync can't detect completed tests).
  const saveReconfig = async () => {
    if (!reconfig) return;
    const named = namedRowsOf(reconfig.rows);
    if (named.length === 0) {
      toast.push("error", "Add at least one named tag.");
      return;
    }
    const cycles = named.filter((r) => r.is_cycle_counter).length;
    if (cycles !== 1) {
      toast.push(
        "error",
        cycles === 0
          ? "Mark exactly one row as the cycle counter (the P_NO==1 trigger the device reads to detect a test)."
          : "Only one row can be the cycle counter.",
      );
      return;
    }
    setSavingTags(true);
    try {
      await tagsApi.replace(gateway.id, {
        tags: toTagInputs(named),
        ...(reconfig.instrumentType ? { instrument_type: reconfig.instrumentType } : {}),
      });
      void qc.invalidateQueries({ queryKey: ["thing-tags", gateway.id] });
      void qc.invalidateQueries({ queryKey: ["things"] });
      toast.push("ok", `Equipment updated for ${gateway.name}`);
      setReconfig(null);
    } catch (err) {
      toast.push("error", backendErrorMessage(err));
    } finally {
      setSavingTags(false);
    }
  };

  // Reset / Re-provision: revoke the cert + mint a fresh claim so the device
  // re-onboards. Confirmed in the ConfirmDialog; the new claim code shows on Overview.
  const handleReprovision = async () => {
    try {
      const res = await things.reprovision(gateway.id, {});
      setMintedClaim({ code: res.claim_code, expiresAt: res.expires_at });
      gateways.setClaimCode(gateway.id, res.claim_code, res.expires_at);
      void qc.invalidateQueries({ queryKey: ["things"] });
      toast.push("ok", `${gateway.name} reset — enter the new claim on the device to re-onboard`);
      setConfirmReprov(false);
      setTab("overview");
    } catch (err) {
      toast.push("error", backendErrorMessage(err));
    }
  };

  // Multi-protocol: which product this Thing is, whether it polls devices (the
  // instruments panel), and the product to offer when its firmware runs more
  // drivers than the product allows.
  const products = productsQ.data?.products ?? [];
  const productId = freshThing.data?.product_id ?? gateway.productId ?? null;
  const product = products.find((p) => p.id === productId) ?? null;
  // Until the catalogue answers, keep today's behaviour (the panel for every gateway)
  // only if the catalogue failed; while it loads, wait.
  const showInstruments = productsQ.isError ? true : productPollsDevices(product);
  const switchTo =
    freshThing.data && isGatewayThing
      ? productSwitchCandidate(
          product,
          products,
          readBirth(freshThing.data).drivers.map((d) => d.id),
        )
      : null;
  const liveStatus = freshThing.data
    ? deriveStatus(freshThing.data.last_seen ?? gateway.lastSeen, freshThing.data.online)
    : gateway.status;
  const canConnect = canCommission && isLiveApi && showInstruments && (isGatewayThing || !freshThing.data);
  const instrumentCount = instrumentsQ.data?.items.length;

  const switchProduct = async (id: string) => {
    if (switchingProduct) return;
    setSwitchingProduct(true);
    setSwitchError(null);
    try {
      await things.update(gateway.id, { product_id: id });
      void qc.invalidateQueries({ queryKey: ["thing", gateway.id] });
      void qc.invalidateQueries({ queryKey: ["thing-drivers", gateway.id] });
      void qc.invalidateQueries({ queryKey: ["things"] });
      toast.push("ok", `${gateway.name} can now take devices on every protocol its firmware runs.`);
    } catch (err) {
      // 422 unknown_product / product_drivers_in_use / product_not_changeable carry an operator-ready detail.
      setSwitchError(errorDetailOf(err) ?? writeErrorMessage(err, "Couldn't change the product."));
    } finally {
      setSwitchingProduct(false);
    }
  };

  // Measure now: ask a claimed gateway to take one immediate Modbus reading of
  // every instrument (rather than waiting for the next poll tick). Fire-and-ack —
  // the device publishes the reading on its own topic; 202 here just confirms the
  // command was published. 409 if the gateway isn't claimed yet.
  const handleMeasure = async () => {
    if (measuring) return;
    setMeasuring(true);
    try {
      await things.measure(gateway.id, {});
      toast.push("ok", `Measurement requested — ${gateway.name} will read + publish now`);
    } catch (err) {
      toast.push("error", backendErrorMessage(err));
    } finally {
      setMeasuring(false);
    }
  };

  // Refresh: this gateway, the rates, its instruments, and the open values and tags read again now (each open view
  // also reads by itself). invalidateQueries reads the views on screen and marks the others stale.
  const refreshAll = async () => {
    const id = gateway.id;
    setRefreshing(true);
    try {
      await Promise.all([
        qc.invalidateQueries({ queryKey: ["thing", id] }),
        qc.invalidateQueries({ queryKey: ["things", "rates"], exact: true }),
        qc.invalidateQueries({ queryKey: ["things", "instruments", id] }),
        qc.invalidateQueries({ queryKey: ["thing-drivers", id] }),
        qc.invalidateQueries({ queryKey: ["thing-tags", id] }),
        qc.invalidateQueries({ queryKey: ["thing-tag-latest", id] }),
        qc.invalidateQueries({ queryKey: ["instrument-tags"] }),
        qc.invalidateQueries({ queryKey: ["instrument-latest"] }),
      ]);
    } finally {
      setRefreshing(false);
    }
  };
  const lastSeen = newestTime(freshThing.data?.last_seen, gateway.lastSeen, rate?.last_reading_at);
  const throughput = rates ? rateSparkline(ratesData, gateway.id) : gateway.msgPerSecHistory;

  const birthProps =
    isLiveApi && freshThing.data && isGatewayThing
      ? {
          thing: freshThing.data,
          drivers: driversQ.data?.drivers,
          driversLoading: driversQ.isLoading,
          product,
          switchTo,
          onSwitchProduct: (id: string) => void switchProduct(id),
          switching: switchingProduct,
          switchError,
        }
      : null;

  return (
    <Sheet open onClose={onClose} widthClass="max-w-xl" ariaLabel={`${gateway.name} detail`}>
      {/* Header */}
      <div className="flex items-start justify-between gap-3 border-b border-border p-5 pb-4">
        <div className="flex flex-1 items-start gap-3">
          <div className="flex size-10 shrink-0 items-center justify-center rounded-md bg-brand-muted text-brand">
            <Cpu className="size-5" />
          </div>
          <div className="flex min-w-0 flex-1 flex-col gap-0.5">
            {edit ? (
              <div className="flex flex-col gap-2">
                <Input
                  value={edit.name}
                  onChange={(e) => setEdit({ ...edit, name: e.target.value })}
                  onKeyDown={(e) => {
                    if (e.key === "Enter") {
                      e.preventDefault();
                      void saveEdit();
                    } else if (e.key === "Escape") {
                      // Esc cancels the rename, not the whole sheet.
                      e.preventDefault();
                      e.stopPropagation();
                      setEdit(null);
                    }
                  }}
                  autoFocus
                  className="h-8 text-sm"
                  aria-label="Gateway name"
                />
                <button
                  type="button"
                  onClick={() => setEdit({ ...edit, simulate: !edit.simulate })}
                  className="flex items-center justify-between gap-2 rounded-md border border-border bg-surface-elevated px-2.5 py-1.5 text-left"
                  aria-pressed={edit.simulate}
                >
                  <span className="text-xs font-medium text-on-surface">Simulate</span>
                  <Toggle on={edit.simulate} />
                </button>
                <div className="flex items-center gap-1.5">
                  <Button size="sm" onClick={() => void saveEdit()} disabled={saving || !edit.name.trim()}>
                    {saving ? <Loader2 className="mr-1 size-3.5 animate-spin" /> : <Check className="mr-1 size-3.5" />}
                    Save
                  </Button>
                  <Button size="sm" variant="ghost" onClick={() => setEdit(null)} disabled={saving}>
                    Cancel
                  </Button>
                </div>
              </div>
            ) : (
              <>
                <h2 className="truncate text-base font-semibold text-on-surface">{gateway.name}</h2>
                <span className="text-[11px] text-on-surface-muted">{gateway.site}</span>
                <div className="mt-1 flex flex-wrap items-center gap-1.5">
                  <StatusPill status={liveStatus} />
                  {getProtocolLabel(gateway.protocol) && (
                    <Badge variant="secondary" className="font-mono uppercase tracking-wide text-[10px]">
                      {getProtocolLabel(gateway.protocol)}
                    </Badge>
                  )}
                  {product && (
                    <Badge variant="outline" className="text-[10px]">
                      {product.label}
                    </Badge>
                  )}
                </div>
              </>
            )}
          </div>
        </div>
        <div className="flex shrink-0 items-center gap-1">
          {isLiveApi && !edit && (
            <RefreshButton
              iconOnly
              busy={refreshing}
              onRefresh={() => void refreshAll()}
              updatedAt={freshThing.dataUpdatedAt}
              now={now}
              ariaLabel={`Refresh ${gateway.name}`}
              hint="Read this gateway again now: its status, reading rate, instruments and their values. The sheet also reads them again by itself while it is open."
            />
          )}
          {!edit && (
            <Button
              variant="ghost"
              size="icon"
              onClick={() => setEdit({ name: gateway.name, simulate: gateway.simulate ?? true })}
              aria-label="Rename or change Simulate"
              title="Rename or change Simulate"
            >
              <Pencil className="size-4" />
            </Button>
          )}
          <Button variant="ghost" size="icon" onClick={onClose} aria-label="Close" title="Close (Esc)">
            <X className="size-4" />
          </Button>
        </div>
      </div>

      {canConnect && !edit && (
        <div className="flex items-center gap-2 border-b border-border px-5 py-2.5">
          <Button asChild size="sm">
            <Link to={connectToGatewayHref(gateway.id)}>
              <Radar className="size-3.5" />
              Connect a device to this gateway
            </Link>
          </Button>
        </div>
      )}

      <Tabs value={tab} onValueChange={(v) => setTab(v as GatewaySheetTab)} className="flex-1">
        <TabsList aria-label={`${gateway.name} sections`} className="px-3">
          <TabsTrigger value="overview">Overview</TabsTrigger>
          <TabsTrigger value="devices" badge={instrumentCount}>
            Devices
          </TabsTrigger>
          <TabsTrigger value="protocols">Protocols</TabsTrigger>
          <TabsTrigger value="settings">Settings</TabsTrigger>
        </TabsList>

        {/* ── Overview ─────────────────────────────────────────────── */}
        <TabsContent value="overview" className="flex flex-col gap-4 p-5">
          <div className="grid grid-cols-2 gap-2 sm:grid-cols-4">
            <Stat label="Tags" value={String(gateway.tags)} mono />
            <Stat label="Readings/s" value={formatRate(liveMsgPerSec)} mono />
            <Stat
              label="Last seen"
              // `now` redraws it as time passes; formatDistanceToNow reads the clock itself.
              // A time a little ahead of this clock reads "less than a minute ago", never "in 1 minute".
              value={lastSeen && now ? formatDistanceToNow(new Date(Math.min(Date.parse(lastSeen), now)), { addSuffix: true }) : "—"}
            />
            <Stat
              label="Cert expires"
              value={
                gateway.certExpiresAt ? formatDistanceToNow(new Date(gateway.certExpiresAt), { addSuffix: true }) : "—"
              }
            />
          </div>

          <section className="flex flex-col gap-1.5 rounded-md border border-border bg-surface-elevated p-3">
            <header className="text-[10px] font-semibold uppercase tracking-wide text-on-surface-muted">
              Readings a second · last 20 minutes
            </header>
            <SparklineCell points={throughput} height={48} ariaLabel={`${gateway.name} reading rate, last 20 minutes`} />
          </section>

          {isLiveApi && freshThing.isLoading && (
            <div className="flex flex-col gap-2" aria-busy="true" aria-label="Loading the gateway">
              <Skeleton className="h-4 w-2/3" />
              <Skeleton className="h-8 w-full" />
            </div>
          )}
          {birthProps && <GatewayBirthPanel {...birthProps} section="overview" />}

          {switchTo && (
            <div className="flex flex-wrap items-center gap-2 rounded-md border border-brand/40 bg-brand-muted/20 p-3 text-xs">
              <span className="min-w-0 flex-1 text-on-surface">
                Its firmware runs more protocols than its product allows.
              </span>
              <Button size="sm" variant="outline" onClick={() => setTab("protocols")}>
                Review
              </Button>
            </div>
          )}

          {claimCode && (
            <ClaimCodePanel
              code={claimCode}
              expiresAt={claimCodeExpiresAt}
              copied={copied}
              regenerating={regenerating}
              onCopy={() => void copyCode()}
              onRegenerate={() => void handleRegenerate()}
            />
          )}

          {/* Onboarding QR codes — scan the mint code + slug into the device's
              captive portal one at a time (firmware R4 req 1), instead of typing a
              32-char code + 24-char slug by hand on a phone. */}
          {claimCode && <OnboardingQR claimCode={claimCode} slug={gateway.slug} />}

          {gateway.twinEntityId && (
            <Button variant="outline" asChild className="self-start">
              <Link to={`/app/twin/equipment?entity=${encodeURIComponent(gateway.twinEntityId)}`}>
                Open on the Equipment page ({gateway.twinEntityName ?? gateway.twinEntityId})
                <ArrowRight className="ml-1.5 size-3.5" />
              </Link>
            </Button>
          )}
        </TabsContent>

        {/* ── Devices ──────────────────────────────────────────────── */}
        <TabsContent value="devices" className="flex flex-col gap-4 p-5">
          {!isLiveApi ? (
            <p className="text-sm text-on-surface-muted">Devices and live values need the live API.</p>
          ) : (
            <>
              {/* Multi-instrument panel — child instruments under a gateway that polls
                  devices (any product with drivers). Each add chains a config_push. */}
              {showInstruments && (
                <GatewayInstruments
                  // One panel per gateway: its default open set, "Show more" and half-filled forms start afresh.
                  key={gateway.id}
                  gatewayId={gateway.id}
                  openValues={openValues?.gatewayId === gateway.id ? openValues.ids : null}
                  onOpenValuesChange={(ids) => setOpenValues({ gatewayId: gateway.id, ids })}
                />
              )}
              {/* Live tag inventory (needs the readings backend). On a gateway that polls instruments, only when the
                  gateway itself has tags: "No tags yet" there read as "no data" while its instruments' points were live. */}
              <ThingTagsSection thingId={gateway.id} hideWhenEmpty={showInstruments} />
            </>
          )}
        </TabsContent>

        {/* ── Protocols ────────────────────────────────────────────── */}
        <TabsContent value="protocols" className="flex flex-col gap-4 p-5">
          {birthProps ? (
            <GatewayBirthPanel {...birthProps} section="protocols" />
          ) : isLiveApi && freshThing.isLoading ? (
            <Skeleton className="h-24 w-full" />
          ) : (
            <p className="text-sm text-on-surface-muted">
              {isLiveApi
                ? "This source is not a gateway, so it has no protocols of its own."
                : "Protocols need the live API."}
            </p>
          )}
        </TabsContent>

        {/* ── Settings ─────────────────────────────────────────────── */}
        <TabsContent value="settings" className="flex flex-col gap-4 p-5">
          <div className="flex flex-col gap-1">
            <span className="text-[10px] font-semibold uppercase tracking-wide text-on-surface-muted">Device ID</span>
            {gateway.slug ? (
              <span
                className="select-all break-all font-mono text-xs text-on-surface"
                title="Device ID — the readings key + on-wire MQTT identity"
              >
                {gateway.slug}
              </span>
            ) : (
              <span className="text-xs text-on-surface-muted">—</span>
            )}
          </div>
          {!claimCode && gateway.slug && <OnboardingQR slug={gateway.slug} />}

          {/* When the gateway may download updates (migration 0262), and what it holds. */}
          {isLiveApi && isGatewayThing && freshThing.data?.update_fetch && (
            <GatewayUpdateFetchPanel
              thing={freshThing.data}
              canEdit={canCommission}
              now={new Date()}
              onSave={async (body) => {
                try {
                  const saved = await things.update(gateway.id, { update_fetch: body });
                  void qc.invalidateQueries({ queryKey: ["thing", gateway.id] });
                  toast.push(
                    "ok",
                    saved.update_fetch?.sent
                      ? `${gateway.name} has the new update setting.`
                      : `Saved. ${gateway.name} gets it when it next comes online.`,
                  );
                } catch (err) {
                  throw new Error(writeErrorMessage(err, "The update setting could not be saved."));
                }
              }}
            />
          )}

          <div className="flex flex-col gap-2">
            {!claimCode && (
              <Button
                variant="outline"
                className="justify-start"
                onClick={() => void handleRegenerate()}
                disabled={regenerating}
              >
                {regenerating ? <Loader2 className="size-3.5 animate-spin" /> : <RefreshCw className="size-3.5" />}
                Mint new claim code
              </Button>
            )}
            {isLiveApi && (
              <Button
                variant="outline"
                className="justify-start"
                disabled={measuring}
                onClick={() => void handleMeasure()}
              >
                {measuring ? <Loader2 className="size-3.5 animate-spin" /> : <Gauge className="size-3.5" />}
                {measuring ? "Requesting…" : "Measure now"}
              </Button>
            )}
            {isLiveApi && (
              <Button
                variant="outline"
                className="justify-start"
                aria-expanded={!!reconfig}
                onClick={() => setReconfig((rc) => (rc ? null : { rows: [blankRow(5)], instrumentType: "" }))}
              >
                <Wrench className="size-3.5" />
                {reconfig ? "Cancel equipment change" : "Change equipment type"}
              </Button>
            )}
          </div>

          {/* Reconfigure (change equipment type / re-import config) */}
          {isLiveApi && reconfig && (
            <section className="flex flex-col gap-3 rounded-md border border-brand/40 bg-brand-muted/10 p-3">
              <header className="flex items-center gap-1.5 text-[10px] font-semibold uppercase tracking-wide text-brand">
                <Wrench className="size-3.5" />
                Change equipment type
              </header>
              <p className="text-[11px] text-on-surface-muted">
                Pick a template or paste the device&apos;s config.txt, and mark one row as the cycle counter. Saving
                replaces this gateway&apos;s tags.
              </p>
              <VSyncInstrumentPicker
                pollDefault={5}
                onRows={(rows) => setReconfig((rc) => (rc ? { ...rc, rows } : rc))}
                onInstrumentType={(id) => setReconfig((rc) => (rc ? { ...rc, instrumentType: id } : rc))}
              />
              <TagRowsEditor
                rows={reconfig.rows}
                onAddRow={() => setReconfig((rc) => (rc ? { ...rc, rows: [...rc.rows, blankRow(5)] } : rc))}
                onRemoveRow={(key) =>
                  setReconfig((rc) => (rc ? { ...rc, rows: rc.rows.filter((r) => r.key !== key) } : rc))
                }
                onPatchRow={(key, patch) =>
                  setReconfig((rc) =>
                    rc ? { ...rc, rows: rc.rows.map((r) => (r.key === key ? { ...r, ...patch } : r)) } : rc,
                  )
                }
                thingSimulate={gateway.simulate ?? false}
                tagNameOptions={[]}
                unitOptions={[]}
              />
              <div className="flex items-center gap-2">
                <Button size="sm" onClick={() => void saveReconfig()} disabled={savingTags}>
                  {savingTags ? (
                    <Loader2 className="mr-1 size-3.5 animate-spin" />
                  ) : (
                    <Check className="mr-1 size-3.5" />
                  )}
                  Save equipment
                </Button>
                <Button size="sm" variant="ghost" onClick={() => setReconfig(null)} disabled={savingTags}>
                  Cancel
                </Button>
              </div>
            </section>
          )}

          {canCommission && (
            <section
              aria-label="Danger zone"
              className="mt-2 flex flex-col gap-3 rounded-md border border-danger/40 bg-danger/5 p-3"
            >
              <header className="flex items-center gap-1.5 text-[10px] font-semibold uppercase tracking-wide text-danger">
                <TriangleAlert className="size-3.5" />
                Danger zone
              </header>
              {isLiveApi && (
                <div className="flex flex-wrap items-center gap-3">
                  <span className="min-w-0 flex-1 text-xs text-on-surface-muted">
                    <span className="font-medium text-on-surface">Reset / Re-provision.</span> Revokes its certificate
                    and mints a new claim code.
                  </span>
                  <Button size="sm" variant="outline" className="text-danger" onClick={() => setConfirmReprov(true)}>
                    <RotateCcw className="size-3.5" />
                    Reset…
                  </Button>
                </div>
              )}
              <div className="flex flex-wrap items-center gap-3">
                <span className="min-w-0 flex-1 text-xs text-on-surface-muted">
                  <span className="font-medium text-on-surface">Remove gateway.</span> Deletes it with its devices and
                  tags.
                </span>
                <Button size="sm" variant="danger" onClick={() => setConfirmDelete(true)}>
                  <Trash2 className="size-3.5" />
                  Remove…
                </Button>
              </div>
            </section>
          )}
        </TabsContent>
      </Tabs>

      <ConfirmDialog
        open={confirmReprov}
        onClose={() => setConfirmReprov(false)}
        onConfirm={handleReprovision}
        title={`Reset ${gateway.name}?`}
        description="This revokes its certificate and mints a new claim code. It stops publishing until the new code is entered on it."
        confirmLabel="Reset gateway"
        destructive
      />
      <ConfirmDialog
        open={confirmDelete}
        onClose={() => setConfirmDelete(false)}
        onConfirm={handleDelete}
        title={`Remove ${gateway.name}?`}
        description="This removes the gateway, its devices and their tags. This can't be undone."
        confirmLabel="Remove"
        destructive
      />
    </Sheet>
  );
}

/** The pending claim code: copy, and mint a new one. */
function ClaimCodePanel({
  code,
  expiresAt,
  copied,
  regenerating,
  onCopy,
  onRegenerate,
}: {
  code: string;
  expiresAt?: string;
  copied: boolean;
  regenerating: boolean;
  onCopy: () => void;
  onRegenerate: () => void;
}) {
  return (
    <section data-copilot-private="" className="flex flex-col gap-2 rounded-md border border-brand/40 bg-brand-muted/20 p-3">
      <div className="flex items-center justify-between gap-2">
        <span className="text-[10px] font-semibold uppercase tracking-wide text-brand">Pending claim</span>
        {expiresAt && (
          <span className="text-[10px] text-on-surface-muted">
            expires {formatDistanceToNow(new Date(expiresAt), { addSuffix: true })}
          </span>
        )}
      </div>
      <div className="flex items-center justify-between gap-2 rounded-md border border-border bg-surface px-3 py-2">
        <span className="break-all font-mono text-lg font-semibold tracking-[0.25em] text-on-surface">{code}</span>
        <div className="flex items-center gap-1">
          <Button size="sm" variant="outline" onClick={onCopy}>
            {copied ? (
              <>
                <Check className="mr-1 size-3.5" />
                Copied
              </>
            ) : (
              <>
                <Copy className="mr-1 size-3.5" />
                Copy
              </>
            )}
          </Button>
          <Button
            size="sm"
            variant="ghost"
            onClick={onRegenerate}
            disabled={regenerating}
            aria-label="Mint a new claim code"
            title="Mint a new claim code"
          >
            {regenerating ? <Loader2 className="size-3.5 animate-spin" /> : <RefreshCw className="size-3.5" />}
          </Button>
        </div>
      </div>
    </section>
  );
}

/**
 * OnboardingQR — two independent "Show QR" buttons (mint code + slug) that reveal
 * ONE QR at a time (firmware R4 req 1). The operator scans one field into the
 * device's captive portal, then the other, without risking scanning both into the
 * wrong box. Each QR encodes ONLY its own value as plain text (no URL / JSON
 * wrapping). Rendered on white so it scans in either light or dark theme.
 */
function OnboardingQR({ claimCode, slug }: { claimCode?: string; slug?: string }) {
  const toast = useToast();
  const [field, setField] = useState<"mint" | "slug" | null>(null);
  const [copied, setCopied] = useState(false);

  if (!claimCode && !slug) return null;

  const value = field === "mint" ? claimCode : field === "slug" ? slug : undefined;

  const copy = async (v: string) => {
    try {
      await navigator.clipboard.writeText(v);
      setCopied(true);
      setTimeout(() => setCopied(false), 1500);
      toast.push("ok", "Copied");
    } catch {
      /* clipboard permission */
    }
  };

  return (
    <section className="flex flex-col gap-2 rounded-md border border-border bg-surface-elevated p-3">
      <header className="text-[10px] font-semibold uppercase tracking-wide text-on-surface-muted">
        Scan to onboard
      </header>
      <p className="text-[11px] text-on-surface-muted">
        Show one code and scan it into the device&apos;s setup portal — one at a time, so the wrong value can&apos;t
        land in the wrong field.
      </p>
      <div className="flex flex-wrap gap-1.5">
        {claimCode && (
          <Button
            size="sm"
            variant="outline"
            onClick={() => setField((f) => (f === "mint" ? null : "mint"))}
            aria-pressed={field === "mint"}
          >
            <QrCode className="mr-1 size-3.5" />
            {field === "mint" ? "Hide QR" : "Show QR for mint code"}
          </Button>
        )}
        {slug && (
          <Button
            size="sm"
            variant="outline"
            onClick={() => setField((f) => (f === "slug" ? null : "slug"))}
            aria-pressed={field === "slug"}
          >
            <QrCode className="mr-1 size-3.5" />
            {field === "slug" ? "Hide QR" : "Show QR for slug"}
          </Button>
        )}
      </div>
      {value && (
        <div className="flex flex-col items-center gap-2 rounded-md border border-border bg-surface p-3">
          <div className="rounded bg-white p-2">
            <QRCodeSVG
              value={value}
              size={176}
              level="M"
              marginSize={2}
              bgColor="#ffffff"
              fgColor="#000000"
              title={field === "mint" ? "Mint code QR" : "Slug QR"}
            />
          </div>
          <div className="flex w-full items-center justify-between gap-2">
            <span className="select-all break-all font-mono text-[11px] text-on-surface">{value}</span>
            <Button size="sm" variant="outline" className="shrink-0" onClick={() => void copy(value)}>
              {copied ? (
                <>
                  <Check className="mr-1 size-3.5" />
                  Copied
                </>
              ) : (
                <>
                  <Copy className="mr-1 size-3.5" />
                  Copy
                </>
              )}
            </Button>
          </div>
          <span className="self-start text-[10px] text-on-surface-muted">
            {field === "mint" ? "Mint / claim code" : "Device slug (device ID)"}
          </span>
        </div>
      )}
    </section>
  );
}

/** The 9 canonical data-kinds offered when authoring a tag in the sheet. */
const TAG_DATA_KINDS: DataKind[] = [
  "analog_scalar",
  "analog_percentage",
  "count_scalar",
  "derived_kpi",
  "categorical_enum",
  "boolean_state",
  "string_label",
  "categorical_distribution",
  "event_stream",
];

/** A tag that never reported answers a null (or zero) time and a null value from the batched latest read. */
function hasReading(latest: PointLatest | undefined): latest is PointLatest & { value: number; time: string } {
  if (!latest || latest.value == null || !latest.time) return false;
  const ms = new Date(latest.time).getTime();
  return Number.isFinite(ms) && ms > 0;
}

function agoLabel(iso: string): string {
  const ms = Date.now() - new Date(iso).getTime();
  if (!Number.isFinite(ms)) return "";
  if (ms < 60_000) return `${Math.max(0, Math.round(ms / 1000))}s ago`;
  if (ms < 3_600_000) return `${Math.round(ms / 60_000)}m ago`;
  return `${Math.round(ms / 3_600_000)}h ago`;
}

function fmtValue(v: number): string {
  return Math.abs(v) >= 100 ? v.toFixed(0) : v.toFixed(2);
}

/**
 * Live tag inventory for a Thing, a page at a time (as the drawer shows an
 * instrument's points): a Thing can carry hundreds of tags, so the list reads
 * one page by name (GET /v1/iot/tags?device_id=…&limit&offset&q), polls that
 * page's latest readings in one batched read (POST /v1/iot/tags:latest) every
 * 5 s while mounted, and supports add / edit-unit-and-poll / remove against
 * the tag CRUD endpoints.
 *
 * `hideWhenEmpty` (a gateway that polls instruments): with no tag of its own and
 * no filter typed, the section shrinks to one line that says the data points
 * are on the instruments, with "Add tag" (the only place a tag is added to the
 * gateway itself), and asks again every 30 s.
 */
function ThingTagsSection({ thingId, hideWhenEmpty = false }: { thingId: string; hideWhenEmpty?: boolean }) {
  const qc = useQueryClient();
  const { filter, setFilter, query, offset, setOffset } = usePointsPage();

  const tagsQuery = useQuery({
    queryKey: ["thing-tags", thingId, query, offset],
    queryFn: () => tagsApi.listByDevice(thingId, { limit: POINTS_PAGE_SIZE, offset, q: query }),
    refetchInterval: (q) => (hideWhenEmpty && !query && q.state.data?.total === 0 ? 30_000 : 5000),
    placeholderData: keepPreviousData,
  });
  const tagList = useMemo(() => tagsQuery.data?.items ?? [], [tagsQuery.data]);
  const tagIds = useMemo(() => tagList.map((t) => t.id), [tagList]);

  // The page's latest readings in one request, polled every 5 s.
  const latestQuery = useQuery({
    queryKey: ["thing-tag-latest", thingId, tagIds.join(",")],
    enabled: tagIds.length > 0,
    refetchInterval: 5000,
    placeholderData: keepPreviousData,
    queryFn: async () => latestByTag((await tagsApi.batchLatest(tagIds)).items),
  });

  const refresh = () => {
    void qc.invalidateQueries({ queryKey: ["thing-tags", thingId] });
    void qc.invalidateQueries({ queryKey: ["thing-tag-latest", thingId] });
    void qc.invalidateQueries({ queryKey: ["things"] });
  };

  const [adding, setAdding] = useState(false);

  if (hideWhenEmpty && !adding && tagsQuery.data?.total === 0 && !filter.trim() && !query && offset === 0) {
    return (
      <div className="flex flex-wrap items-center justify-between gap-2 px-1 text-[11px] text-on-surface-muted">
        <span>The gateway has no tags of its own: its data points are on its instruments.</span>
        <Button size="sm" variant="ghost" className="h-6" onClick={() => setAdding(true)}>
          <Plus className="mr-1 size-3.5" />
          Add tag
        </Button>
      </div>
    );
  }

  return (
    <section className="flex flex-col gap-2 rounded-md border border-border bg-surface-elevated p-3">
      <header className="flex items-center justify-between gap-2">
        <span className="text-[10px] font-semibold uppercase tracking-wide text-on-surface-muted">
          Tags · live values
        </span>
        <div className="flex items-center gap-1">
          {tagsQuery.isFetching && <Loader2 className="size-3 animate-spin text-on-surface-muted" />}
          <Button size="sm" variant="ghost" className="h-6" onClick={() => setAdding((v) => !v)}>
            <Plus className="mr-1 size-3.5" />
            Add tag
          </Button>
        </div>
      </header>

      {adding && (
        <AddTagRow
          thingId={thingId}
          onDone={(ok) => {
            setAdding(false);
            if (ok) refresh();
          }}
        />
      )}

      {valuesReadFailing(latestQuery) && (
        <ValuesReadFailed
          shownAt={latestQuery.data ? latestQuery.dataUpdatedAt : 0}
          onRetry={() => latestQuery.refetch()}
          subject="the values of the gateway's tags"
        />
      )}

      <ThingTagsList
        state={tagsQuery.data ? "ready" : tagsQuery.isError ? "failed" : "loading"}
        items={tagList}
        total={tagsQuery.data?.total ?? 0}
        offset={tagsQuery.data?.offset ?? offset}
        filter={filter}
        latest={latestQuery.data ?? {}}
        onFilter={setFilter}
        onPage={setOffset}
        onChanged={refresh}
        onRetry={() => void tagsQuery.refetch()}
      />
    </section>
  );
}

const TAG_NOUN = { one: "tag", many: "tags" };

export interface ThingTagsListProps {
  state: "loading" | "failed" | "ready";
  /** The page's tags. */
  items: TagOption[];
  /** Every tag that matches the filter. */
  total: number;
  offset: number;
  /** The name filter as typed. */
  filter: string;
  /** Latest reading by tag id; a tag missing from it has none yet. */
  latest: Record<string, PointLatest | undefined>;
  onFilter: (text: string) => void;
  onPage: (offset: number) => void;
  /** A row was edited or removed. */
  onChanged: () => void;
  /** Read the tags again after a failed read (a "Try again" button); without it the text says to reopen. */
  onRetry?: () => void;
}

/** One page of a Thing's tags with the filter and the pager (ThingTagsSection holds the page and the reads). */
export function ThingTagsList(p: ThingTagsListProps) {
  // The filter stays while one is typed, so clearing it never strands the person on a narrowed list.
  const filterable = p.total > POINTS_PAGE_SIZE || p.filter.trim() !== "";
  return (
    <div className="flex flex-col gap-1.5">
      {filterable && <PointsFilter value={p.filter} onChange={p.onFilter} noun={TAG_NOUN} />}
      {p.state === "loading" ? (
        <div className="flex items-center gap-2 py-2 text-xs text-on-surface-muted">
          <Loader2 className="size-3.5 animate-spin" /> Loading tags…
        </div>
      ) : p.state === "failed" ? (
        <p role="alert" className="flex flex-wrap items-center gap-2 py-1 text-xs text-danger">
          {p.onRetry ? "The tags could not be read." : "The tags could not be read. Close and open the gateway again to retry."}
          {p.onRetry && (
            <Button size="sm" variant="ghost" className="h-6 px-1.5 text-xs" onClick={p.onRetry}>
              Try again
            </Button>
          )}
        </p>
      ) : p.items.length === 0 && p.offset > 0 ? (
        // Past the end: tags were removed while this page was open.
        <p className="flex items-center gap-2 py-1 text-xs text-on-surface-muted">
          No tags on this page.
          <Button size="sm" variant="ghost" className="h-6 px-1.5 text-xs" onClick={() => p.onPage(0)}>
            First page
          </Button>
        </p>
      ) : p.items.length === 0 ? (
        <p className="py-1 text-xs text-on-surface-muted">
          {p.filter.trim()
            ? `No tag matches “${p.filter.trim()}”.`
            : "No tags yet. Add one above to start publishing data."}
        </p>
      ) : (
        <ul className="flex flex-col divide-y divide-border">
          {p.items.map((t) => (
            <TagRow key={t.id} tag={t} latest={p.latest[t.id]} onChanged={p.onChanged} />
          ))}
        </ul>
      )}
      {p.state === "ready" && p.items.length > 0 && (
        <PointsPager offset={p.offset} shown={p.items.length} total={p.total} onPage={p.onPage} noun={TAG_NOUN} />
      )}
    </div>
  );
}

/** One tag row: name + data_kind, latest value + "live · Xs ago", edit, remove. */
function TagRow({ tag, latest, onChanged }: { tag: TagOption; latest: PointLatest | undefined; onChanged: () => void }) {
  const toast = useToast();
  const [editing, setEditing] = useState(false);
  const [unit, setUnit] = useState(tag.unit ?? "");
  const [pollSec, setPollSec] = useState("");
  const [minV, setMinV] = useState("");
  const [maxV, setMaxV] = useState("");
  const [busy, setBusy] = useState(false);

  const quality = readingQuality(latest, tag.poll_sec, undefined, tag.max_silence_sec);

  const save = async () => {
    setBusy(true);
    try {
      const poll = Number(pollSec);
      const lo = Number(minV);
      const hi = Number(maxV);
      await tagsApi.update(tag.id, {
        unit: unit.trim(),
        ...(pollSec.trim() !== "" && Number.isFinite(poll) && poll > 0 ? { poll_sec: poll } : {}),
        ...(minV.trim() !== "" && Number.isFinite(lo) ? { min_value: lo } : {}),
        ...(maxV.trim() !== "" && Number.isFinite(hi) ? { max_value: hi } : {}),
      });
    } catch (err) {
      setBusy(false);
      toast.push("error", backendErrorMessage(err));
      return;
    }
    setBusy(false);
    setEditing(false);
    toast.push("ok", `Updated ${tag.name}`);
    onChanged();
  };

  const [confirmRemove, setConfirmRemove] = useState(false);
  const remove = async () => {
    setBusy(true);
    try {
      await tagsApi.remove(tag.id);
    } catch (err) {
      setBusy(false);
      toast.push("error", backendErrorMessage(err));
      return;
    }
    setBusy(false);
    setConfirmRemove(false);
    toast.push("ok", `Removed ${tag.name}`);
    onChanged();
  };

  return (
    <li className="flex flex-col gap-1 py-1.5">
      <div className="flex items-center gap-2">
        <span className="truncate font-mono text-xs text-on-surface">{tag.name}</span>
        <Badge variant="secondary" className="font-mono text-[8px]">
          {tag.data_kind}
        </Badge>
        <div className="ml-auto flex items-center gap-1.5">
          {hasReading(latest) ? (
            <>
              <Wifi className="size-3.5 text-success" />
              <span className="font-mono text-xs font-semibold text-on-surface">
                {fmtValue(latest.value)}
                {tag.unit ? ` ${tag.unit}` : ""}
              </span>
              <span className="text-[10px] text-on-surface-muted">
                {quality.state === "stale" ? agoLabel(latest.time) : `live · ${agoLabel(latest.time)}`}
              </span>
              {quality.badge && (
                <Badge
                  variant={quality.state === "stale" ? "danger" : "warning"}
                  className="text-[8px]"
                  title={quality.hint}
                >
                  {quality.badge}
                </Badge>
              )}
            </>
          ) : (
            <span className="text-[10px] text-on-surface-muted">no data yet</span>
          )}
          <button
            type="button"
            onClick={() => {
              setUnit(tag.unit ?? "");
              setPollSec(tag.poll_sec != null ? String(tag.poll_sec) : "");
              setMinV(tag.min_value != null ? String(tag.min_value) : "");
              setMaxV(tag.max_value != null ? String(tag.max_value) : "");
              setEditing((v) => !v);
            }}
            className="text-on-surface-muted hover:text-on-surface"
            title="Edit tag"
            aria-label={`Edit ${tag.name}`}
          >
            <Pencil className="size-3.5" />
          </button>
          <button
            type="button"
            onClick={() => setConfirmRemove(true)}
            disabled={busy}
            className="text-on-surface-muted hover:text-danger"
            title="Remove tag"
            aria-label={`Remove ${tag.name}`}
          >
            <Trash2 className="size-3.5" />
          </button>
        </div>
      </div>

      {editing && (
        <div className="flex flex-wrap items-center gap-2 pl-1">
          <label className="flex items-center gap-1 text-[10px] text-on-surface-muted">
            unit
            <Input
              value={unit}
              onChange={(e) => setUnit(e.target.value)}
              placeholder="°C"
              className="h-7 w-16 text-xs"
            />
          </label>
          <label className="flex items-center gap-1 text-[10px] text-on-surface-muted">
            poll (s)
            <Input
              type="number"
              min={1}
              value={pollSec}
              onChange={(e) => setPollSec(e.target.value)}
              placeholder="—"
              className="h-7 w-16 text-xs"
            />
          </label>
          <label className="flex items-center gap-1 text-[10px] text-on-surface-muted">
            min
            <Input
              type="number"
              value={minV}
              onChange={(e) => setMinV(e.target.value)}
              placeholder="—"
              className="h-7 w-16 text-xs"
            />
          </label>
          <label className="flex items-center gap-1 text-[10px] text-on-surface-muted">
            max
            <Input
              type="number"
              value={maxV}
              onChange={(e) => setMaxV(e.target.value)}
              placeholder="—"
              className="h-7 w-16 text-xs"
            />
          </label>
          <Button size="sm" className="h-7" onClick={() => void save()} disabled={busy}>
            {busy ? <Loader2 className="size-3.5 animate-spin" /> : "Save"}
          </Button>
          <Button size="sm" variant="ghost" className="h-7" onClick={() => setEditing(false)} disabled={busy}>
            Cancel
          </Button>
          {tag.simulate && (
            <p className="w-full text-[10px] text-on-surface-muted">
              Simulated tag — changing min/max rescales the generated signal to the new band.
            </p>
          )}
        </div>
      )}
      <ConfirmDialog
        open={confirmRemove}
        onClose={() => setConfirmRemove(false)}
        onConfirm={remove}
        title={`Remove tag ${tag.name}?`}
        description="This removes the tag from this gateway."
        confirmLabel="Remove tag"
        destructive
      />
    </li>
  );
}

/** Inline "add a tag" form: name + data_kind + unit → POST /v1/iot/tags. */
function AddTagRow({ thingId, onDone }: { thingId: string; onDone: (created: boolean) => void }) {
  const toast = useToast();
  const [name, setName] = useState("");
  const [dataKind, setDataKind] = useState<DataKind>("analog_scalar");
  const [unit, setUnit] = useState("");
  const [busy, setBusy] = useState(false);

  const create = async () => {
    const nm = name.trim();
    if (!nm) return;
    setBusy(true);
    try {
      await tagsApi.create({
        device_id: thingId,
        name: nm,
        data_kind: dataKind,
        ...(unit.trim() ? { unit: unit.trim() } : {}),
      });
    } catch (err) {
      setBusy(false);
      toast.push("error", backendErrorMessage(err));
      return;
    }
    setBusy(false);
    toast.push("ok", `Added ${nm}`);
    onDone(true);
  };

  return (
    <div className="flex flex-wrap items-center gap-2 rounded-md border border-dashed border-border p-2">
      <Input
        value={name}
        onChange={(e) => setName(e.target.value)}
        placeholder="Tag name (e.g. bath_temp)"
        className="h-7 flex-1 min-w-[120px] text-xs"
        autoFocus
        onKeyDown={(e) => {
          if (e.key === "Enter") {
            e.preventDefault();
            void create();
          }
        }}
      />
      <select
        value={dataKind}
        onChange={(e) => setDataKind(e.target.value as DataKind)}
        className="h-7 rounded-md border border-border bg-surface px-2 text-xs text-on-surface"
        aria-label="Data kind"
      >
        {TAG_DATA_KINDS.map((k) => (
          <option key={k} value={k}>
            {DATA_KIND_LABEL[k]}
          </option>
        ))}
      </select>
      <Input value={unit} onChange={(e) => setUnit(e.target.value)} placeholder="unit" className="h-7 w-16 text-xs" />
      <Button size="sm" className="h-7" onClick={() => void create()} disabled={busy || !name.trim()}>
        {busy ? <Loader2 className="size-3.5 animate-spin" /> : <Check className="size-3.5" />}
      </Button>
      <Button size="sm" variant="ghost" className="h-7" onClick={() => onDone(false)} disabled={busy}>
        <X className="size-3.5" />
      </Button>
    </div>
  );
}

function Stat({ label, value, mono }: { label: string; value: string; mono?: boolean }) {
  return (
    <div className="flex flex-col gap-0.5 rounded-md border border-border bg-surface-elevated px-2 py-1.5">
      <span className="text-[10px] uppercase tracking-wide text-on-surface-muted">{label}</span>
      <span className={cn("text-sm font-medium text-on-surface", mono && "font-mono")}>{value}</span>
    </div>
  );
}

function Toggle({ on }: { on: boolean }) {
  return (
    <span
      className={cn(
        "relative inline-flex h-5 w-9 shrink-0 items-center rounded-full transition-colors",
        on ? "bg-brand" : "bg-surface-muted",
      )}
    >
      <span
        className={cn(
          "inline-block size-4 transform rounded-full bg-surface shadow transition-transform",
          on ? "translate-x-4" : "translate-x-0.5",
        )}
      />
    </span>
  );
}
